import {
  ChangeDetectorRef,
  Component,
  computed,
  HostListener,
  inject,
  OnDestroy,
  Signal,
  signal,
  viewChild,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import {ProcessHeaderComponent} from "./process-header/process-header.component";
import {EsvgFiles} from 'frontier/nucleus';
import {Observable, Subscription,} from "rxjs";
import {OpenItemsComponent} from "./open-items/open-items.component";
import {filter, take} from "rxjs/operators";
import {DocumentOutboxComponent} from "./document-outbox/document-outbox.component";
import {TShownTables} from './shown-tables.type';
import {CartDetailsComponent} from "./cart-details/cart-details.component";
import {ClientsComponent} from './clients/clients.component';
import {CartsComponent} from './carts/carts.component';
import {ECalculusStateFilter} from '../../config/state-filter.enum';
import {toObservable} from '@angular/core/rxjs-interop';
import {ProcessOverviewStore} from './service/process-overview.store';
import {CalculusStore} from '../../../Services/calculus.store';
import {PrimeTemplate} from 'primeng/api';
import {SplitterModule} from 'primeng/splitter';
import {BreadCrumbComponent} from './bread-crumb/bread-crumb.component';
import {JsonPipe} from '@angular/common';
import {MatTab, MatTabContent, MatTabGroup, MatTabLabel} from '@angular/material/tabs';
import {MatIcon} from '@angular/material/icon';
import {IPdfOverlayToggleEvent} from './carts/pdf-overlay-toggle-event.interface';
import {ICart, IClient, IInvoice, IPdfOverlay, IProcessState, IShownTableTab} from './service/state.interface';
import {TabActivatedDirective} from "frontier/browserkit/src/lib/directives/tab-activated.directive";

@Component({
  selector: 'kpi4me-process-overview',
  templateUrl: './process-overview.component.html',
  styleUrls: ['./process-overview.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    ProcessHeaderComponent,
    BreadCrumbComponent,
    SplitterModule,
    PrimeTemplate,
    ClientsComponent,
    DocumentOutboxComponent,
    OpenItemsComponent,
    CartsComponent,
    CartDetailsComponent,
    JsonPipe,
    MatTabGroup,
    MatTab,
    MatTabLabel,
    MatIcon,
    MatTabContent,
    TabActivatedDirective,
  ],
})
export class ProcessOverviewComponent implements OnDestroy {
  private _processOverviewService: ProcessOverviewStore = inject(ProcessOverviewStore);
  calculusStore: CalculusStore = inject(CalculusStore);
  private _calculusService: CalculusStore = inject(CalculusStore);
  private _cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  protected readonly EsvgFiles = EsvgFiles;
  protected readonly ECalculusStateFilter = ECalculusStateFilter;

  selectedProcess: Signal<IProcessState> = this._processOverviewService.selectedProcess;
  tableTabs: Signal<Set<TShownTables>> = this._processOverviewService.tableTabs;
  tableTabsArray: Signal<IShownTableTab[]> = this._processOverviewService.tableTabsArray;
  selectedCart: Signal<ICart> = this._processOverviewService.selectedCart;
  selectedClient: Signal<IClient> = this._processOverviewService.selectedClient;
  selectedInvoice: Signal<IInvoice> = this._processOverviewService.selectedInvoice;
  pdfOverlay: Signal<IPdfOverlay> = this._processOverviewService.pdfOverlay;
  customPageSize: Signal<number> = this._calculusService.customPageSize;
  preventUpdate: WritableSignal<boolean> = signal<boolean>(false);
  selectedTableIndex: Signal<number> = this._processOverviewService.selectedTableIndex
  clientsRef: Signal<ClientsComponent> = viewChild(ClientsComponent);
  cartsRef: Signal<CartsComponent> = viewChild(CartsComponent);
  openItemsRef: Signal<OpenItemsComponent> = viewChild(OpenItemsComponent);
  documentOutboxRef: Signal<DocumentOutboxComponent> = viewChild(DocumentOutboxComponent);
  isPdfLoading$: Observable<boolean> = toObservable(this._processOverviewService.isPdfLoading);

  detailsRef: Signal<CartDetailsComponent> = viewChild('details');

  subs: Subscription = new Subscription();

  detailsActive: Signal<boolean> = computed(() => {
    return !!(this.selectedCart() || this.selectedClient());
  });

  @HostListener('window:keydown.control.alt.u', ['$event']) preventUpdateToggle() {
    this.preventUpdate.set(!this.preventUpdate());
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  toggleDocumentDisplay(IPdfOverlayToggleEvent: IPdfOverlayToggleEvent): void {
    console.log('toggleDocumentDisplay', IPdfOverlayToggleEvent);
    // Show the loading indicator
    this._processOverviewService.isPdfLoading$.next(true);
    // When the pdf loading process is finished
    this.isPdfLoading$
      .pipe(
        filter((v: boolean) => v === false),
        take(1),
      )
      .subscribe(() => {
        this.openItemsRef()?.gridApi?.hideOverlay();
        this._cdr.detectChanges();
      });
    switch (IPdfOverlayToggleEvent.type) {
      case "collectiveInvoice":
        this.cartsRef().pdfCollectiveInvoiceToggle$.emit(IPdfOverlayToggleEvent);
        break;
      case "detailDocument":
        this.cartsRef().pdfDetailDocumentToggle$.emit(IPdfOverlayToggleEvent);
        break;
      case "detailTrapoDocument":
        this.cartsRef().pdfDetailTrapoDocumentToggle$.emit(IPdfOverlayToggleEvent);
        break;
    }

    this._cdr.detectChanges();
  }

  resizeStarted(): void {
    this.cartsRef()?.isDragging.set(true);
  }

  resizeEnded(): void {
    this.cartsRef()?.isDragging.set(false);
    setTimeout((): void => {
      this.detailsRef()?.conspicuityDetail()?.resized();
    }, 50)
  }

  onSelectedTableIndexChange(idx: number): void {
    const selectedProcess: IProcessState = this.selectedProcess();
    switch (selectedProcess.category) {
      case ECalculusStateFilter.ETLnMapping:
        this._processOverviewService.selectedTableInEtlnMapping$.next(idx);
        break;
      case ECalculusStateFilter.arrangeInvoice:
        this._processOverviewService.selectedTableInArrangeInvoice$.next(idx);
        break;
      case ECalculusStateFilter.monitorPayment:
        this._processOverviewService.selectedTableInMonitorPayment$.next(idx);
        break;
    }
    if (idx === 0) {
      this._processOverviewService.selectedInvoice$.next(null);
      this._processOverviewService.selectedClient$.next(null);
    } else {
      this._processOverviewService.selectedCart$.next(null);
    }
  }

  fitColumns(): void {
    setTimeout(() => {
      [this.clientsRef(), this.cartsRef(), this.documentOutboxRef(), this.openItemsRef()]
        .forEach((ref: ClientsComponent | CartsComponent | DocumentOutboxComponent | OpenItemsComponent) => {
          ref?.fitColumns();
        });
    }, 50);
  }

  showHelpForTables(): void {
    this.calculusStore.helpRoute$.next('prozessübersicht.html#tabellenansicht');
  }
}
